import type { Comp, Wire, WireKind } from "./engine";

export interface ExampleNote {
  id: string;
  x: number;
  y: number;
  text: string;
  color?: string;
  size?: number;
}

export interface ExampleDoc {
  name: string;
  comps: Comp[];
  wires: Wire[];
  notes?: ExampleNote[];
}

export interface CircuitExample {
  id: string;
  title: string;
  description: string;
  doc: ExampleDoc;
}

const PHASE_COLORS = ["#d63c34", "#e2b93b", "#2f6fd0"] as const;
const CONTROL = "#1c1f24";
const NEUTRAL = "#2f6fd0";
const EARTH = "#3fae4b";

function c(
  id: string,
  type: string,
  x: number,
  y: number,
  label: string,
  state: Comp["state"] = {},
): Comp {
  return {
    id,
    type,
    x,
    y,
    rot: 0,
    label,
    state: {
      closed: type === "mcb_1p" || type === "mcb_3p" || type === "mccb_3p",
      pressed: false,
      tripped: false,
      elapsed: 0,
      pos: 0,
      ...state,
    },
  };
}

function w(
  id: string,
  a: string,
  at: string,
  b: string,
  bt: string,
  kind: WireKind,
  color?: string,
  mid?: { x: number; y: number },
): Wire {
  return {
    id,
    from: { c: a, t: at },
    to: { c: b, t: bt },
    kind,
    ...(color ? { color } : {}),
    ...(mid ? { mid } : {}),
  };
}

const note = (id: string, x: number, y: number, text: string, size = 22): ExampleNote => ({
  id,
  x,
  y,
  text,
  size,
  color: "#ea7215",
});

function dolExample(): CircuitExample {
  const comps: Comp[] = [
    c("dol-src", "src_3ph", 80, 40, "G1"),
    c("dol-q1", "mcb_3p", 95, 220, "Q1"),
    c("dol-k1", "contactor", 80, 410, "KM1"),
    c("dol-ol", "overload", 80, 760, "F1"),
    c("dol-m1", "motor_3ph", 90, 1060, "M1"),

    c("dol-ctrl", "src_ctrl_220", 500, 60, "G2"),
    c("dol-qc", "mcb_1p", 530, 215, "Q2"),
    c("dol-stop", "pb_stop", 515, 400, "S0 STOP"),
    c("dol-start", "pb_start", 515, 565, "S1 START"),
  ];

  const wires: Wire[] = [
    w("d01", "dol-src", "L1", "dol-q1", "1", "phase", PHASE_COLORS[0]),
    w("d02", "dol-src", "L2", "dol-q1", "3", "phase", PHASE_COLORS[1]),
    w("d03", "dol-src", "L3", "dol-q1", "5", "phase", PHASE_COLORS[2]),
    w("d04", "dol-q1", "2", "dol-k1", "L1", "phase", PHASE_COLORS[0]),
    w("d05", "dol-q1", "4", "dol-k1", "L2", "phase", PHASE_COLORS[1]),
    w("d06", "dol-q1", "6", "dol-k1", "L3", "phase", PHASE_COLORS[2]),
    w("d07", "dol-k1", "T1", "dol-ol", "L1", "phase", PHASE_COLORS[0]),
    w("d08", "dol-k1", "T2", "dol-ol", "L2", "phase", PHASE_COLORS[1]),
    w("d09", "dol-k1", "T3", "dol-ol", "L3", "phase", PHASE_COLORS[2]),
    w("d10", "dol-ol", "T1", "dol-m1", "U1", "phase", PHASE_COLORS[0]),
    w("d11", "dol-ol", "T2", "dol-m1", "V1", "phase", PHASE_COLORS[1]),
    w("d12", "dol-ol", "T3", "dol-m1", "W1", "phase", PHASE_COLORS[2]),
    w("d13", "dol-src", "PE", "dol-m1", "PE", "earth", EARTH, { x: 260, y: 1050 }),

    w("d14", "dol-ctrl", "L", "dol-qc", "1", "control", CONTROL),
    w("d15", "dol-qc", "2", "dol-stop", "1", "control", CONTROL),
    w("d16", "dol-stop", "2", "dol-ol", "95", "control", CONTROL, { x: 430, y: 700 }),
    w("d17", "dol-ol", "96", "dol-start", "3", "control", CONTROL, { x: 450, y: 640 }),
    w("d18", "dol-start", "4", "dol-k1", "A1", "control", CONTROL),
    w("d19", "dol-ol", "96", "dol-k1", "13", "control", CONTROL, { x: 390, y: 590 }),
    w("d20", "dol-k1", "14", "dol-k1", "A1", "control", CONTROL, { x: 330, y: 520 }),
    w("d21", "dol-ctrl", "N", "dol-k1", "A2", "neutral", NEUTRAL, { x: 720, y: 500 }),
  ];

  return {
    id: "dol",
    title: "تشغيل موتور START / STOP",
    description: "MCB + كونتكتر + أوفرلود + تثبيت ذاتي 13/14.",
    doc: {
      name: "مثال - تشغيل موتور START STOP",
      comps,
      wires,
      notes: [note("dn1", 100, 10, "دائرة الباور"), note("dn2", 520, 20, "دائرة السيطرة")],
    },
  };
}

function reverseExample(): CircuitExample {
  const comps: Comp[] = [
    c("rv-src", "src_3ph", 80, 30, "G1"),
    c("rv-q1", "mcb_3p", 95, 205, "Q1"),
    c("rv-kf", "contactor", 20, 390, "KM1 FWD"),
    c("rv-kr", "contactor", 245, 390, "KM2 REV"),
    c("rv-ol", "overload", 135, 760, "F1"),
    c("rv-m1", "motor_3ph", 145, 1060, "M1"),

    c("rv-ctrl", "src_ctrl_220", 630, 45, "G2"),
    c("rv-qc", "mcb_1p", 660, 200, "Q2"),
    c("rv-stop", "pb_stop", 650, 375, "S0 STOP"),
    c("rv-fwd", "pb_start", 565, 555, "S1 FWD"),
    c("rv-rev", "pb_start", 740, 555, "S2 REV"),
  ];

  const wires: Wire[] = [
    w("r01", "rv-src", "L1", "rv-q1", "1", "phase", PHASE_COLORS[0]),
    w("r02", "rv-src", "L2", "rv-q1", "3", "phase", PHASE_COLORS[1]),
    w("r03", "rv-src", "L3", "rv-q1", "5", "phase", PHASE_COLORS[2]),

    w("r04", "rv-q1", "2", "rv-kf", "L1", "phase", PHASE_COLORS[0]),
    w("r05", "rv-q1", "4", "rv-kf", "L2", "phase", PHASE_COLORS[1]),
    w("r06", "rv-q1", "6", "rv-kf", "L3", "phase", PHASE_COLORS[2]),
    w("r07", "rv-q1", "2", "rv-kr", "L1", "phase", PHASE_COLORS[0]),
    w("r08", "rv-q1", "4", "rv-kr", "L2", "phase", PHASE_COLORS[1]),
    w("r09", "rv-q1", "6", "rv-kr", "L3", "phase", PHASE_COLORS[2]),

    w("r10", "rv-kf", "T1", "rv-ol", "L1", "phase", PHASE_COLORS[0]),
    w("r11", "rv-kf", "T2", "rv-ol", "L2", "phase", PHASE_COLORS[1]),
    w("r12", "rv-kf", "T3", "rv-ol", "L3", "phase", PHASE_COLORS[2]),
    w("r13", "rv-kr", "T1", "rv-ol", "L3", "phase", PHASE_COLORS[0]),
    w("r14", "rv-kr", "T2", "rv-ol", "L2", "phase", PHASE_COLORS[1]),
    w("r15", "rv-kr", "T3", "rv-ol", "L1", "phase", PHASE_COLORS[2]),
    w("r16", "rv-ol", "T1", "rv-m1", "U1", "phase", PHASE_COLORS[0]),
    w("r17", "rv-ol", "T2", "rv-m1", "V1", "phase", PHASE_COLORS[1]),
    w("r18", "rv-ol", "T3", "rv-m1", "W1", "phase", PHASE_COLORS[2]),
    w("r19", "rv-src", "PE", "rv-m1", "PE", "earth", EARTH, { x: 420, y: 1060 }),

    w("r20", "rv-ctrl", "L", "rv-qc", "1", "control", CONTROL),
    w("r21", "rv-qc", "2", "rv-stop", "1", "control", CONTROL),
    w("r22", "rv-stop", "2", "rv-ol", "95", "control", CONTROL, { x: 545, y: 720 }),

    w("r23", "rv-ol", "96", "rv-fwd", "3", "control", CONTROL, { x: 520, y: 620 }),
    w("r24", "rv-ol", "96", "rv-kf", "13", "control", CONTROL, { x: 500, y: 545 }),
    w("r25", "rv-fwd", "4", "rv-kr", "21", "control", CONTROL),
    w("r26", "rv-kf", "14", "rv-kr", "21", "control", CONTROL, { x: 500, y: 500 }),
    w("r27", "rv-kr", "22", "rv-kf", "A1", "control", CONTROL, { x: 440, y: 485 }),

    w("r28", "rv-ol", "96", "rv-rev", "3", "control", CONTROL, { x: 775, y: 650 }),
    w("r29", "rv-ol", "96", "rv-kr", "13", "control", CONTROL, { x: 785, y: 525 }),
    w("r30", "rv-rev", "4", "rv-kf", "21", "control", CONTROL),
    w("r31", "rv-kr", "14", "rv-kf", "21", "control", CONTROL, { x: 790, y: 480 }),
    w("r32", "rv-kf", "22", "rv-kr", "A1", "control", CONTROL, { x: 520, y: 460 }),
    w("r33", "rv-ctrl", "N", "rv-kf", "A2", "neutral", NEUTRAL, { x: 900, y: 450 }),
    w("r34", "rv-ctrl", "N", "rv-kr", "A2", "neutral", NEUTRAL, { x: 900, y: 500 }),
  ];

  return {
    id: "reverse",
    title: "عكس اتجاه دوران موتور",
    description: "كونتكتر أمامي وعكسي مع تبديل فازتين وإنترلوك كهربائي.",
    doc: {
      name: "مثال - عكس اتجاه دوران موتور",
      comps,
      wires,
      notes: [note("rn1", 120, 5, "دائرة الباور"), note("rn2", 660, 10, "دائرة السيطرة")],
    },
  };
}

function starDeltaExample(): CircuitExample {
  const comps: Comp[] = [
    c("sd-src", "src_3ph", 80, 20, "G1"),
    c("sd-q1", "mcb_3p", 95, 190, "Q1"),
    c("sd-main", "contactor", 75, 365, "KM1 MAIN"),
    c("sd-ol", "overload", 75, 710, "F1"),
    c("sd-m", "motor_3ph_6t", 80, 1010, "M1 STAR/DELTA"),
    c("sd-star", "contactor", 330, 820, "KM2 STAR"),
    c("sd-delta", "contactor", 565, 820, "KM3 DELTA"),

    c("sd-ctrl", "src_ctrl_220", 790, 20, "G2"),
    c("sd-qc", "mcb_1p", 820, 170, "Q2"),
    c("sd-stop", "pb_stop", 810, 340, "S0 STOP"),
    c("sd-start", "pb_start", 810, 500, "S1 START"),
    c("sd-t", "timer", 790, 680, "KT1", { delay: 3000, coilVoltage: "220V AC" }),
  ];

  const wires: Wire[] = [
    w("s01", "sd-src", "L1", "sd-q1", "1", "phase", PHASE_COLORS[0]),
    w("s02", "sd-src", "L2", "sd-q1", "3", "phase", PHASE_COLORS[1]),
    w("s03", "sd-src", "L3", "sd-q1", "5", "phase", PHASE_COLORS[2]),
    w("s04", "sd-q1", "2", "sd-main", "L1", "phase", PHASE_COLORS[0]),
    w("s05", "sd-q1", "4", "sd-main", "L2", "phase", PHASE_COLORS[1]),
    w("s06", "sd-q1", "6", "sd-main", "L3", "phase", PHASE_COLORS[2]),
    w("s07", "sd-main", "T1", "sd-ol", "L1", "phase", PHASE_COLORS[0]),
    w("s08", "sd-main", "T2", "sd-ol", "L2", "phase", PHASE_COLORS[1]),
    w("s09", "sd-main", "T3", "sd-ol", "L3", "phase", PHASE_COLORS[2]),
    w("s10", "sd-ol", "T1", "sd-m", "U1", "phase", PHASE_COLORS[0]),
    w("s11", "sd-ol", "T2", "sd-m", "V1", "phase", PHASE_COLORS[1]),
    w("s12", "sd-ol", "T3", "sd-m", "W1", "phase", PHASE_COLORS[2]),
    w("s13", "sd-src", "PE", "sd-m", "PE", "earth", EARTH, { x: 310, y: 1060 }),

    // كونتكتر STAR: نوحّد مداخل الأقطاب الثلاثة، ثم توصل المخارج U2/V2/W2 عند عمله.
    w("s14", "sd-star", "L1", "sd-star", "L2", "phase", CONTROL),
    w("s15", "sd-star", "L2", "sd-star", "L3", "phase", CONTROL),
    w("s16", "sd-star", "T1", "sd-m", "U2", "phase", CONTROL),
    w("s17", "sd-star", "T2", "sd-m", "V2", "phase", CONTROL),
    w("s18", "sd-star", "T3", "sd-m", "W2", "phase", CONTROL),

    // كونتكتر DELTA حسب ربط المحرك القياسي الذي يدعمه المحاكي:
    // U1-W2 / V1-U2 / W1-V2.
    w("s19", "sd-delta", "L1", "sd-m", "V1", "phase", PHASE_COLORS[1]),
    w("s20", "sd-delta", "T1", "sd-m", "U2", "phase", PHASE_COLORS[1]),
    w("s21", "sd-delta", "L2", "sd-m", "W1", "phase", PHASE_COLORS[2]),
    w("s22", "sd-delta", "T2", "sd-m", "V2", "phase", PHASE_COLORS[2]),
    w("s23", "sd-delta", "L3", "sd-m", "U1", "phase", PHASE_COLORS[0]),
    w("s24", "sd-delta", "T3", "sd-m", "W2", "phase", PHASE_COLORS[0]),

    // السيطرة MAIN + self hold + timer.
    w("s25", "sd-ctrl", "L", "sd-qc", "1", "control", CONTROL),
    w("s26", "sd-qc", "2", "sd-stop", "1", "control", CONTROL),
    w("s27", "sd-stop", "2", "sd-ol", "95", "control", CONTROL, { x: 720, y: 640 }),
    w("s28", "sd-ol", "96", "sd-start", "3", "control", CONTROL, { x: 720, y: 560 }),
    w("s29", "sd-start", "4", "sd-main", "A1", "control", CONTROL, { x: 690, y: 505 }),
    w("s30", "sd-ol", "96", "sd-main", "13", "control", CONTROL, { x: 665, y: 520 }),
    w("s31", "sd-main", "14", "sd-main", "A1", "control", CONTROL, { x: 620, y: 465 }),
    w("s32", "sd-main", "A1", "sd-t", "2", "control", CONTROL, { x: 700, y: 635 }),
    w("s33", "sd-ctrl", "N", "sd-main", "A2", "neutral", NEUTRAL, { x: 1050, y: 460 }),
    w("s34", "sd-ctrl", "N", "sd-t", "7", "neutral", NEUTRAL, { x: 1050, y: 735 }),

    // STAR أولاً عبر NC للتايمر + NC للـ DELTA.
    w("s35", "sd-main", "14", "sd-t", "1", "control", CONTROL, { x: 660, y: 620 }),
    w("s36", "sd-t", "4", "sd-delta", "21", "control", CONTROL, { x: 700, y: 860 }),
    w("s37", "sd-delta", "22", "sd-star", "A1", "control", CONTROL),
    w("s38", "sd-ctrl", "N", "sd-star", "A2", "neutral", NEUTRAL, { x: 1050, y: 980 }),

    // DELTA بعد انتهاء الزمن عبر NO للتايمر + NC للـ STAR.
    w("s39", "sd-main", "14", "sd-t", "8", "control", CONTROL, { x: 670, y: 650 }),
    w("s40", "sd-t", "6", "sd-star", "21", "control", CONTROL, { x: 760, y: 900 }),
    w("s41", "sd-star", "22", "sd-delta", "A1", "control", CONTROL),
    w("s42", "sd-ctrl", "N", "sd-delta", "A2", "neutral", NEUTRAL, { x: 1050, y: 1040 }),
  ];

  return {
    id: "star-delta",
    title: "Star / Delta أوتوماتيك",
    description: "MAIN + STAR + DELTA + Timer + Overload مع إنترلوك كامل.",
    doc: {
      name: "مثال - Star Delta أوتوماتيك",
      comps,
      wires,
      notes: [note("sn1", 120, -5, "دائرة الباور"), note("sn2", 810, -5, "دائرة السيطرة")],
    },
  };
}

function sequenceExample(): CircuitExample {
  const comps: Comp[] = [
    c("sq-src", "src_3ph", 80, 25, "G1"),
    c("sq-q1", "mcb_3p", 95, 200, "Q1"),
    c("sq-k1", "contactor", 20, 385, "KM1"),
    c("sq-k2", "contactor", 245, 385, "KM2"),
    c("sq-ol1", "overload", 20, 730, "F1"),
    c("sq-ol2", "overload", 245, 730, "F2"),
    c("sq-m1", "motor_3ph", 30, 1030, "M1"),
    c("sq-m2", "motor_3ph", 255, 1030, "M2"),

    c("sq-ctrl", "src_ctrl_220", 620, 35, "G2"),
    c("sq-qc", "mcb_1p", 650, 190, "Q2"),
    c("sq-stop", "pb_stop", 640, 365, "S0 STOP"),
    c("sq-start", "pb_start", 640, 525, "S1 START"),
    c("sq-t", "timer", 620, 690, "KT1", { delay: 3000, coilVoltage: "220V AC" }),
  ];

  const wires: Wire[] = [
    w("q01", "sq-src", "L1", "sq-q1", "1", "phase", PHASE_COLORS[0]),
    w("q02", "sq-src", "L2", "sq-q1", "3", "phase", PHASE_COLORS[1]),
    w("q03", "sq-src", "L3", "sq-q1", "5", "phase", PHASE_COLORS[2]),
    w("q04", "sq-q1", "2", "sq-k1", "L1", "phase", PHASE_COLORS[0]),
    w("q05", "sq-q1", "4", "sq-k1", "L2", "phase", PHASE_COLORS[1]),
    w("q06", "sq-q1", "6", "sq-k1", "L3", "phase", PHASE_COLORS[2]),
    w("q07", "sq-q1", "2", "sq-k2", "L1", "phase", PHASE_COLORS[0]),
    w("q08", "sq-q1", "4", "sq-k2", "L2", "phase", PHASE_COLORS[1]),
    w("q09", "sq-q1", "6", "sq-k2", "L3", "phase", PHASE_COLORS[2]),
    w("q10", "sq-k1", "T1", "sq-ol1", "L1", "phase", PHASE_COLORS[0]),
    w("q11", "sq-k1", "T2", "sq-ol1", "L2", "phase", PHASE_COLORS[1]),
    w("q12", "sq-k1", "T3", "sq-ol1", "L3", "phase", PHASE_COLORS[2]),
    w("q13", "sq-k2", "T1", "sq-ol2", "L1", "phase", PHASE_COLORS[0]),
    w("q14", "sq-k2", "T2", "sq-ol2", "L2", "phase", PHASE_COLORS[1]),
    w("q15", "sq-k2", "T3", "sq-ol2", "L3", "phase", PHASE_COLORS[2]),
    w("q16", "sq-ol1", "T1", "sq-m1", "U1", "phase", PHASE_COLORS[0]),
    w("q17", "sq-ol1", "T2", "sq-m1", "V1", "phase", PHASE_COLORS[1]),
    w("q18", "sq-ol1", "T3", "sq-m1", "W1", "phase", PHASE_COLORS[2]),
    w("q19", "sq-ol2", "T1", "sq-m2", "U1", "phase", PHASE_COLORS[0]),
    w("q20", "sq-ol2", "T2", "sq-m2", "V1", "phase", PHASE_COLORS[1]),
    w("q21", "sq-ol2", "T3", "sq-m2", "W1", "phase", PHASE_COLORS[2]),
    w("q22", "sq-src", "PE", "sq-m1", "PE", "earth", EARTH),
    w("q23", "sq-src", "PE", "sq-m2", "PE", "earth", EARTH),

    w("q24", "sq-ctrl", "L", "sq-qc", "1", "control", CONTROL),
    w("q25", "sq-qc", "2", "sq-stop", "1", "control", CONTROL),
    w("q26", "sq-stop", "2", "sq-ol1", "95", "control", CONTROL, { x: 540, y: 650 }),
    w("q27", "sq-ol1", "96", "sq-ol2", "95", "control", CONTROL, { x: 500, y: 780 }),
    w("q28", "sq-ol2", "96", "sq-start", "3", "control", CONTROL, { x: 520, y: 620 }),
    w("q29", "sq-start", "4", "sq-k1", "A1", "control", CONTROL),
    w("q30", "sq-ol2", "96", "sq-k1", "13", "control", CONTROL, { x: 500, y: 545 }),
    w("q31", "sq-k1", "14", "sq-k1", "A1", "control", CONTROL, { x: 465, y: 500 }),
    w("q32", "sq-k1", "A1", "sq-t", "2", "control", CONTROL, { x: 540, y: 670 }),
    w("q33", "sq-k1", "14", "sq-t", "8", "control", CONTROL, { x: 520, y: 640 }),
    w("q34", "sq-t", "6", "sq-k2", "A1", "control", CONTROL, { x: 540, y: 830 }),
    w("q35", "sq-ctrl", "N", "sq-k1", "A2", "neutral", NEUTRAL, { x: 880, y: 470 }),
    w("q36", "sq-ctrl", "N", "sq-t", "7", "neutral", NEUTRAL, { x: 880, y: 760 }),
    w("q37", "sq-ctrl", "N", "sq-k2", "A2", "neutral", NEUTRAL, { x: 880, y: 520 }),
  ];

  return {
    id: "sequence-2",
    title: "تشغيل موتورين بالتتابع",
    description: "M1 يعمل أولاً، وبعد 3 ثوانٍ يعمل M2 تلقائياً.",
    doc: {
      name: "مثال - تشغيل موتورين بالتتابع",
      comps,
      wires,
      notes: [note("qn1", 120, 0, "دائرة الباور"), note("qn2", 650, 0, "دائرة السيطرة")],
    },
  };
}

function ats2Example(): CircuitExample {
  const comps: Comp[] = [
    // المصدر الوطني: ثلاث فازات + نيوترال مستقل حتى دخل الكونتكتر الرباعي.
    c("a2-s1", "src_3ph", 20, 20, "وطني"),
    c("a2-q1", "mcb_3p", 35, 190, "Q1 وطني"),
    c("a2-k1", "contactor_small", 20, 390, "KM1 وطني"),
    c("a2-pf1", "phase_failure", 245, 245, "PF1 وطني", { protectionDelay: 1500 }),
    c("a2-r1", "relay", 265, 650, "K1 ريليه الوطنية", { coilVoltage: "220V AC" }),

    // المولد: نيوتراله مستقل تماماً عن نيوترال الوطنية قبل خرج الـATS.
    c("a2-s2", "src_3ph", 570, 20, "مولد"),
    c("a2-q2", "mcb_3p", 585, 190, "Q2 مولد"),
    c("a2-k2", "contactor_small", 570, 390, "KM2 مولد"),
    c("a2-pf2", "phase_failure", 795, 245, "PF2 مولد", { protectionDelay: 1500 }),
    c("a2-r2", "relay", 815, 650, "K2 ريليه المولد", { coilVoltage: "220V AC" }),

    // بدل المحرك: ثلاث لمبات إشارة، كل لمبة على فيز مستقل من خرج الـATS.
    c("a2-lr", "pilot_red", 315, 1080, "L1 أحمر"),
    c("a2-ly", "pilot_amber", 485, 1080, "L2 أصفر"),
    c("a2-lb", "pilot_blue", 655, 1080, "L3 أزرق"),
  ];

  const wires: Wire[] = [];
  let n = 1;
  const add = (a: string, at: string, b: string, bt: string, kind: WireKind, color?: string, mid?: { x: number; y: number }) =>
    wires.push(w(`a2${String(n++).padStart(2, "0")}`, a, at, b, bt, kind, color, mid));

  // =========================
  // POWER - الوطنية
  // =========================
  add("a2-s1", "L1", "a2-q1", "1", "phase", PHASE_COLORS[0]);
  add("a2-s1", "L2", "a2-q1", "3", "phase", PHASE_COLORS[1]);
  add("a2-s1", "L3", "a2-q1", "5", "phase", PHASE_COLORS[2]);
  add("a2-q1", "2", "a2-k1", "L1", "phase", PHASE_COLORS[0]);
  add("a2-q1", "4", "a2-k1", "L2", "phase", PHASE_COLORS[1]);
  add("a2-q1", "6", "a2-k1", "L3", "phase", PHASE_COLORS[2]);
  add("a2-s1", "N", "a2-k1", "L4", "neutral", NEUTRAL);

  // Phase Failure الوطنية يأخذ L1/L2/L3 + N من نفس المصدر فقط.
  add("a2-q1", "2", "a2-pf1", "L1", "phase", PHASE_COLORS[0]);
  add("a2-q1", "4", "a2-pf1", "L2", "phase", PHASE_COLORS[1]);
  add("a2-q1", "6", "a2-pf1", "L3", "phase", PHASE_COLORS[2]);
  add("a2-s1", "N", "a2-pf1", "N", "neutral", NEUTRAL);

  // =========================
  // POWER - المولد
  // =========================
  add("a2-s2", "L1", "a2-q2", "1", "phase", PHASE_COLORS[0]);
  add("a2-s2", "L2", "a2-q2", "3", "phase", PHASE_COLORS[1]);
  add("a2-s2", "L3", "a2-q2", "5", "phase", PHASE_COLORS[2]);
  add("a2-q2", "2", "a2-k2", "L1", "phase", PHASE_COLORS[0]);
  add("a2-q2", "4", "a2-k2", "L2", "phase", PHASE_COLORS[1]);
  add("a2-q2", "6", "a2-k2", "L3", "phase", PHASE_COLORS[2]);
  add("a2-s2", "N", "a2-k2", "L4", "neutral", NEUTRAL);

  // Phase Failure المولد يأخذ تغذيته ونيوتراله من المولد فقط.
  add("a2-q2", "2", "a2-pf2", "L1", "phase", PHASE_COLORS[0]);
  add("a2-q2", "4", "a2-pf2", "L2", "phase", PHASE_COLORS[1]);
  add("a2-q2", "6", "a2-pf2", "L3", "phase", PHASE_COLORS[2]);
  add("a2-s2", "N", "a2-pf2", "N", "neutral", NEUTRAL);

  // =========================
  // CONTROL - الوطنية
  // PF1 NO -> كويل ريليه الوطنية. البارد من نفس الوطنية.
  // =========================
  add("a2-s1", "L1", "a2-pf1", "2", "control", PHASE_COLORS[0]);
  add("a2-pf1", "3", "a2-r1", "13", "control", CONTROL);
  add("a2-r1", "14", "a2-s1", "N", "neutral", NEUTRAL);

  // حار الوطنية -> NO ريليه الوطنية -> NC ريليه المولد -> NC كونتكتر المولد -> كويل كونتكتر الوطنية.
  add("a2-s1", "L1", "a2-r1", "9", "control", PHASE_COLORS[0]);
  add("a2-r1", "5", "a2-r2", "11", "control", CONTROL);
  add("a2-r2", "3", "a2-k2", "21", "control", CONTROL);
  add("a2-k2", "22", "a2-k1", "A1", "control", CONTROL);
  add("a2-k1", "A2", "a2-s1", "N", "neutral", NEUTRAL);

  // =========================
  // CONTROL - المولد
  // PF2 NO -> NC ريليه الوطنية -> NC كونتكتر الوطنية -> كويل ريليه المولد.
  // =========================
  add("a2-s2", "L1", "a2-pf2", "2", "control", PHASE_COLORS[0]);
  add("a2-pf2", "3", "a2-r1", "10", "control", CONTROL);
  add("a2-r1", "2", "a2-k1", "21", "control", CONTROL);
  add("a2-k1", "22", "a2-r2", "13", "control", CONTROL);
  add("a2-r2", "14", "a2-s2", "N", "neutral", NEUTRAL);

  // NO ريليه المولد يغذي كويل كونتكتر المولد، والبارد من المولد نفسه.
  add("a2-s2", "L1", "a2-r2", "9", "control", PHASE_COLORS[0]);
  add("a2-r2", "5", "a2-k2", "A1", "control", CONTROL);
  add("a2-k2", "A2", "a2-s2", "N", "neutral", NEUTRAL);

  // =========================
  // خرج ATS إلى ثلاث لمبات Single Phase.
  // L1 أحمر / L2 أصفر / L3 أزرق، والنيوترال يمر عبر القطب الرابع.
  // =========================
  for (const k of ["a2-k1", "a2-k2"] as const) {
    add(k, "T1", "a2-lr", "X1", "phase", PHASE_COLORS[0]);
    add(k, "T2", "a2-ly", "X1", "phase", PHASE_COLORS[1]);
    add(k, "T3", "a2-lb", "X1", "phase", PHASE_COLORS[2]);
    add(k, "T4", "a2-lr", "X2", "neutral", NEUTRAL);
    add(k, "T4", "a2-ly", "X2", "neutral", NEUTRAL);
    add(k, "T4", "a2-lb", "X2", "neutral", NEUTRAL);
  }

  return {
    id: "ats-2",
    title: "ATS مصدرين: وطني + مولد",
    description: "كونتكترات رباعية + Phase Failure + ريليه مستقل لكل مصدر مع NO/NC وإنترلوك مزدوج، وثلاث لمبات حمل L1/L2/L3.",
    doc: {
      name: "مثال - ATS وطني ومولد بالريليهات",
      comps,
      wires,
      notes: [
        note("a2n1", 20, -10, "المصدر الوطني - نيوترال مستقل"),
        note("a2n2", 570, -10, "المولد - نيوترال مستقل"),
        note("a2n3", 365, 1015, "يروح للسيركت او لصندوق توزيع الاحمال"),
      ],
    },
  };
}

function ats3Example(): CircuitExample {
  const comps: Comp[] = [
    // المصدر الأول: الوطنية - الأولوية الأولى.
    c("a3-s1", "src_3ph", 0, 20, "وطني"),
    c("a3-q1", "mcb_3p", 15, 190, "Q1 وطني"),
    c("a3-k1", "contactor_small", 0, 390, "KM1 وطني"),
    c("a3-p1", "phase_failure", 215, 245, "PF1 وطني", { protectionDelay: 1200 }),
    c("a3-r1", "relay", 235, 650, "K1 ريليه الوطنية", { coilVoltage: "220V AC" }),
    c("a3-ax1", "aux_block", 30, 690, "AUX KM1", { linkTo: "a3-k1" }),

    // المصدر الثاني: المولد - الأولوية الثانية.
    c("a3-s2", "src_3ph", 650, 20, "مولد"),
    c("a3-q2", "mcb_3p", 665, 190, "Q2 مولد"),
    c("a3-k2", "contactor_small", 650, 390, "KM2 مولد"),
    c("a3-p2", "phase_failure", 865, 245, "PF2 مولد", { protectionDelay: 1200 }),
    c("a3-r2", "relay", 885, 650, "K2 ريليه المولد", { coilVoltage: "220V AC" }),
    c("a3-ax2", "aux_block", 680, 690, "AUX KM2", { linkTo: "a3-k2" }),

    // المصدر الثالث: الخط الثاني - الأولوية الثالثة.
    c("a3-s3", "src_3ph", 1300, 20, "الخط الثاني"),
    c("a3-q3", "mcb_3p", 1315, 190, "Q3 خط ثاني"),
    c("a3-k3", "contactor_small", 1300, 390, "KM3 خط ثاني"),
    c("a3-p3", "phase_failure", 1515, 245, "PF3 خط ثاني", { protectionDelay: 1200 }),
    c("a3-r3", "relay", 1535, 650, "K3 ريليه الخط الثاني", { coilVoltage: "220V AC" }),
    c("a3-ax3", "aux_block", 1330, 690, "AUX KM3", { linkTo: "a3-k3" }),

    // حمل Single Phase موزع على الفازات الثلاث بدل المحرك.
    c("a3-lr", "pilot_red", 650, 1110, "L1 أحمر"),
    c("a3-ly", "pilot_amber", 820, 1110, "L2 أصفر"),
    c("a3-lb", "pilot_blue", 990, 1110, "L3 أزرق"),
  ];

  const wires: Wire[] = [];
  let n = 1;
  const add = (a: string, at: string, b: string, bt: string, kind: WireKind, color?: string, mid?: { x: number; y: number }) =>
    wires.push(w(`a3${String(n++).padStart(2, "0")}`, a, at, b, bt, kind, color, mid));

  // =========================
  // POWER + مراقبة الفازات لكل مصدر.
  // النيوترال لا يختلط بين المصادر، وكل واحد يدخل L4 للكونتكتر الرباعي الخاص به.
  // =========================
  for (const [src, q, k, pf] of [
    ["a3-s1", "a3-q1", "a3-k1", "a3-p1"],
    ["a3-s2", "a3-q2", "a3-k2", "a3-p2"],
    ["a3-s3", "a3-q3", "a3-k3", "a3-p3"],
  ] as const) {
    add(src, "L1", q, "1", "phase", PHASE_COLORS[0]);
    add(src, "L2", q, "3", "phase", PHASE_COLORS[1]);
    add(src, "L3", q, "5", "phase", PHASE_COLORS[2]);
    add(q, "2", k, "L1", "phase", PHASE_COLORS[0]);
    add(q, "4", k, "L2", "phase", PHASE_COLORS[1]);
    add(q, "6", k, "L3", "phase", PHASE_COLORS[2]);
    add(src, "N", k, "L4", "neutral", NEUTRAL);

    add(q, "2", pf, "L1", "phase", PHASE_COLORS[0]);
    add(q, "4", pf, "L2", "phase", PHASE_COLORS[1]);
    add(q, "6", pf, "L3", "phase", PHASE_COLORS[2]);
    add(src, "N", pf, "N", "neutral", NEUTRAL);
  }

  // =========================
  // ريليه الوطنية: PF1 NO يغذي الكويل مباشرة من نفس المصدر.
  // =========================
  add("a3-s1", "L1", "a3-p1", "2", "control", PHASE_COLORS[0]);
  add("a3-p1", "3", "a3-r1", "13", "control", CONTROL);
  add("a3-r1", "14", "a3-s1", "N", "neutral", NEUTRAL);

  // NO ريليه الوطنية -> NC ريليه المولد -> NC AUX المولد -> NC ريليه الخط الثاني -> NC AUX الخط الثاني -> KM1.
  add("a3-s1", "L1", "a3-r1", "9", "control", PHASE_COLORS[0]);
  add("a3-r1", "5", "a3-r2", "11", "control", CONTROL);
  add("a3-r2", "3", "a3-ax2", "71", "control", CONTROL);
  add("a3-ax2", "72", "a3-r3", "11", "control", CONTROL);
  add("a3-r3", "3", "a3-ax3", "71", "control", CONTROL);
  add("a3-ax3", "72", "a3-k1", "A1", "control", CONTROL);
  add("a3-k1", "A2", "a3-s1", "N", "neutral", NEUTRAL);

  // =========================
  // ريليه المولد: PF2 NO -> NC ريليه الوطنية -> NC كونتكتر الوطنية -> كويل ريليه المولد.
  // =========================
  add("a3-s2", "L1", "a3-p2", "2", "control", PHASE_COLORS[0]);
  add("a3-p2", "3", "a3-r1", "10", "control", CONTROL);
  add("a3-r1", "2", "a3-k1", "21", "control", CONTROL);
  add("a3-k1", "22", "a3-r2", "13", "control", CONTROL);
  add("a3-r2", "14", "a3-s2", "N", "neutral", NEUTRAL);

  // NO ريليه المولد -> NC ريليه الخط الثاني -> NC AUX الخط الثاني -> KM2.
  add("a3-s2", "L1", "a3-r2", "9", "control", PHASE_COLORS[0]);
  add("a3-r2", "5", "a3-r3", "12", "control", CONTROL);
  add("a3-r3", "4", "a3-ax3", "81", "control", CONTROL);
  add("a3-ax3", "82", "a3-k2", "A1", "control", CONTROL);
  add("a3-k2", "A2", "a3-s2", "N", "neutral", NEUTRAL);

  // =========================
  // ريليه الخط الثاني - حسب التسلسل المطلوب:
  // PF3 NO -> NC ريليه الوطنية -> NC AUX الوطنية -> NC ريليه المولد -> NC AUX المولد -> كويل ريليه الخط الثاني.
  // =========================
  add("a3-s3", "L1", "a3-p3", "2", "control", PHASE_COLORS[0]);
  add("a3-p3", "3", "a3-r1", "11", "control", CONTROL);
  add("a3-r1", "3", "a3-ax1", "61", "control", CONTROL);
  add("a3-ax1", "62", "a3-r2", "10", "control", CONTROL);
  add("a3-r2", "2", "a3-ax2", "61", "control", CONTROL);
  add("a3-ax2", "62", "a3-r3", "13", "control", CONTROL);
  add("a3-r3", "14", "a3-s3", "N", "neutral", NEUTRAL);

  // NO ريليه الخط الثاني يغذي كونتكتر الخط الثاني مباشرة.
  add("a3-s3", "L1", "a3-r3", "9", "control", PHASE_COLORS[0]);
  add("a3-r3", "5", "a3-k3", "A1", "control", CONTROL);
  add("a3-k3", "A2", "a3-s3", "N", "neutral", NEUTRAL);

  // =========================
  // خرج ATS: أربع أقطاب من كل كونتكتر إلى نفس الحمل.
  // فقط بعد الكونتكتر يمكن أن يلتقي خرج النيوترال، لذلك لا يوجد نيوترال مشترك بين المصادر قبل التحويل.
  // =========================
  for (const k of ["a3-k1", "a3-k2", "a3-k3"] as const) {
    add(k, "T1", "a3-lr", "X1", "phase", PHASE_COLORS[0]);
    add(k, "T2", "a3-ly", "X1", "phase", PHASE_COLORS[1]);
    add(k, "T3", "a3-lb", "X1", "phase", PHASE_COLORS[2]);
    add(k, "T4", "a3-lr", "X2", "neutral", NEUTRAL);
    add(k, "T4", "a3-ly", "X2", "neutral", NEUTRAL);
    add(k, "T4", "a3-lb", "X2", "neutral", NEUTRAL);
  }

  return {
    id: "ats-3",
    title: "ATS ثلاث مصادر: وطني + مولد + خط ثاني",
    description: "كونتكترات رباعية، Phase Failure وريليه مستقل لكل مصدر، أولوية وطني ثم مولد ثم الخط الثاني، وثلاث لمبات L1/L2/L3.",
    doc: {
      name: "مثال - ATS ثلاث مصادر بالريليهات",
      comps,
      wires,
      notes: [
        note("a3n1", 0, -10, "وطني - أولوية 1"),
        note("a3n2", 650, -10, "مولد - أولوية 2"),
        note("a3n3", 1300, -10, "خط ثاني - أولوية 3"),
        note("a3n4", 700, 1045, "يروح للسيركت او لصندوق توزيع الاحمال"),
      ],
    },
  };
}


function trafficLightExample(): CircuitExample {
  const comps: Comp[] = [
    c("tl-src", "src_ctrl_220", 1650, 1220, "G1 سيطرة 220V"),
    c("tl-q1", "mcb_1p", 1665, 1390, "Q1 إشارة المرور"),
  ];

  const stages = [
    { street: 1, color: "أحمر", type: "pilot_red", delay: 2500, lampX: 650, lampY: 90 },
    { street: 1, color: "أصفر", type: "pilot_amber", delay: 1200, lampX: 650, lampY: 205 },
    { street: 1, color: "أخضر", type: "pilot_green", delay: 2500, lampX: 650, lampY: 320 },

    { street: 2, color: "أحمر", type: "pilot_red", delay: 2500, lampX: 1050, lampY: 405 },
    { street: 2, color: "أصفر", type: "pilot_amber", delay: 1200, lampX: 1050, lampY: 520 },
    { street: 2, color: "أخضر", type: "pilot_green", delay: 2500, lampX: 1050, lampY: 635 },

    { street: 3, color: "أحمر", type: "pilot_red", delay: 2500, lampX: 650, lampY: 720 },
    { street: 3, color: "أصفر", type: "pilot_amber", delay: 1200, lampX: 650, lampY: 835 },
    { street: 3, color: "أخضر", type: "pilot_green", delay: 2500, lampX: 650, lampY: 950 },

    { street: 4, color: "أحمر", type: "pilot_red", delay: 2500, lampX: 250, lampY: 405 },
    { street: 4, color: "أصفر", type: "pilot_amber", delay: 1200, lampX: 250, lampY: 520 },
    { street: 4, color: "أخضر", type: "pilot_green", delay: 2500, lampX: 250, lampY: 635 },
  ] as const;

  const controlX = [80, 455, 830, 1205] as const;
  const controlY = [1230, 1515, 1800] as const;

  stages.forEach((stage, i) => {
    const streetIndex = stage.street - 1;
    const stageIndex = i % 3;
    const x = controlX[streetIndex];
    const y = controlY[stageIndex];

    comps.push(
      c(`tl-l${i + 1}`, stage.type, stage.lampX, stage.lampY, `شارع ${stage.street} - ${stage.color}`),
      c(`tl-t${i + 1}`, "timer", x, y, `KT${i + 1} شارع ${stage.street} ${stage.color}`, {
        delay: stage.delay,
        coilVoltage: "220V AC",
      }),
      c(`tl-r${i + 1}`, "relay", x + 185, y - 10, `K${i + 1} شارع ${stage.street} ${stage.color}`, {
        coilVoltage: "220V AC",
      }),
    );
  });

  // تايمر Reset قصير بعد نهاية الشارع الرابع. يبقي التغذية مفصولة لحظة
  // حتى تصفر جميع تايمرات المراحل، ثم يعيد الدورة تلقائياً من الشارع الأول.
  comps.push(
    c("tl-reset", "timer_off", 1660, 1600, "KT13 RESET الدورة", {
      delay: 650,
      coilVoltage: "220V AC",
    }),
  );

  const wires: Wire[] = [];
  let n = 1;
  const add = (
    a: string,
    at: string,
    b: string,
    bt: string,
    kind: WireKind,
    color?: string,
    mid?: { x: number; y: number },
  ) => wires.push(w(`tl${String(n++).padStart(3, "0")}`, a, at, b, bt, kind, color, mid));

  // =========================
  // مصدر السيطرة الرئيسي
  // =========================
  add("tl-src", "L", "tl-q1", "1", "control", CONTROL);

  // النيوترال مشترك داخل دائرة إشارة المرور لكل ملفات التايمرات والريليهات واللمبات.
  for (let i = 1; i <= 12; i++) {
    add("tl-src", "N", `tl-t${i}`, "7", "neutral", NEUTRAL);
    add("tl-src", "N", `tl-r${i}`, "14", "neutral", NEUTRAL);
    add("tl-src", "N", `tl-l${i}`, "X2", "neutral", NEUTRAL);

    // كل ريليه يشغل لمبة مرحلته من نقطة NO 9→5.
    add("tl-q1", "2", `tl-r${i}`, "9", "control", CONTROL);
    add(`tl-r${i}`, "5", `tl-l${i}`, "X1", "control", CONTROL);
  }
  add("tl-src", "N", "tl-reset", "7", "neutral", NEUTRAL);

  // =========================
  // بداية الدورة
  // Q1 -> NC تايمر RESET -> KT1
  // =========================
  add("tl-q1", "2", "tl-reset", "1", "control", CONTROL);
  add("tl-reset", "4", "tl-t1", "2", "control", CONTROL);
  add("tl-reset", "4", "tl-t1", "1", "control", CONTROL);
  add("tl-reset", "4", "tl-t1", "8", "control", CONTROL);

  // =========================
  // 12 مرحلة متتابعة
  // أثناء عدّ KT: نقطة NC 1→4 تشغل ريليه المرحلة واللمبة.
  // بعد انتهاء الزمن: نقطة NO 8→6 تبدأ التايمر التالي.
  // =========================
  for (let i = 1; i <= 12; i++) {
    add(`tl-t${i}`, "4", `tl-r${i}`, "13", "control", CONTROL);

    if (i < 12) {
      add(`tl-t${i}`, "6", `tl-t${i + 1}`, "2", "control", CONTROL);
      add(`tl-t${i}`, "6", `tl-t${i + 1}`, "1", "control", CONTROL);
      add(`tl-t${i}`, "6", `tl-t${i + 1}`, "8", "control", CONTROL);
    }
  }

  // المرحلة الأخيرة KT12 تشغل تايمر OFF-Delay للـRESET عبر NO 8→6.
  // RESET يفتح NC 1→4 للحظات، فتسقط كل المراحل ويتصفر العد، ثم تبدأ KT1 من جديد.
  add("tl-t12", "6", "tl-reset", "2", "control", CONTROL);

  return {
    id: "traffic-light-4",
    title: "دائرة إشارة المرور - 4 شوارع",
    description: "12 لمبة (أحمر/أصفر/أخضر) تعمل بالتتابع على 4 شوارع وتعيد الدورة تلقائياً بالتايمرات والريليهات.",
    doc: {
      name: "مثال - دائرة إشارة المرور 4 شوارع",
      comps,
      wires,
      notes: [
        note("tln1", 620, 25, "الشارع 1"),
        note("tln2", 1035, 350, "الشارع 2"),
        note("tln3", 620, 1065, "الشارع 3"),
        note("tln4", 235, 350, "الشارع 4"),
        note("tln5", 580, 520, "تقاطع 4 شوارع", 26),
        note("tln6", 80, 1160, "دائرة التحكم - Timers + Relays"),
        note("tln7", 1630, 1160, "المصدر + RESET تلقائي"),
      ],
    },
  };
}


function bridgeCraneExample(): CircuitExample {
  const comps: Comp[] = [
    // =========================
    // POWER - مصدر ثلاثي وقاطع رئيسي
    // =========================
    c("cr-src", "src_3ph", 70, 20, "G1 مصدر الكرين"),
    c("cr-q1", "mcb_3p", 90, 190, "Q1 MAIN"),

    // محور 1: حركة الجسر يمين / يسار
    c("cr-k1r", "contactor", 20, 390, "KM1 يمين"),
    c("cr-k1l", "contactor", 245, 390, "KM2 يسار"),
    c("cr-ol1", "overload", 130, 735, "F1 Bridge"),
    c("cr-m1", "motor_3ph", 140, 1035, "M1 يمين / يسار"),

    // محور 2: حركة العربة أمام / خلف
    c("cr-k2f", "contactor", 500, 390, "KM3 أمام"),
    c("cr-k2b", "contactor", 725, 390, "KM4 خلف"),
    c("cr-ol2", "overload", 610, 735, "F2 Trolley"),
    c("cr-m2", "motor_3ph", 620, 1035, "M2 أمام / خلف"),

    // محور 3: الرفع أعلى / أسفل
    c("cr-k3u", "contactor", 980, 390, "KM5 أعلى"),
    c("cr-k3d", "contactor", 1205, 390, "KM6 أسفل"),
    c("cr-ol3", "overload", 1090, 735, "F3 Hoist"),
    c("cr-m3", "motor_3ph", 1100, 1035, "M3 أعلى / أسفل"),

    // =========================
    // CONTROL - تحكم Pendant لحظي مع Emergency Stop
    // =========================
    c("cr-ctrl", "src_ctrl_220", 1540, 25, "G2 CONTROL 220V"),
    c("cr-qc", "mcb_1p", 1570, 185, "Q2 CONTROL"),
    c("cr-estop", "estop", 1550, 350, "S0 EMERGENCY STOP"),

    c("cr-br", "pb_start", 1470, 560, "S1 يمين"),
    c("cr-bl", "pb_start", 1660, 560, "S2 يسار"),

    c("cr-bf", "pb_start", 1470, 760, "S3 أمام"),
    c("cr-bb", "pb_start", 1660, 760, "S4 خلف"),

    c("cr-bu", "pb_start", 1470, 960, "S5 أعلى"),
    c("cr-bd", "pb_start", 1660, 960, "S6 أسفل"),
  ];

  const wires: Wire[] = [];
  let n = 1;
  const add = (
    a: string,
    at: string,
    b: string,
    bt: string,
    kind: WireKind,
    color?: string,
    mid?: { x: number; y: number },
  ) => wires.push(w(`cr${String(n++).padStart(2, "0")}`, a, at, b, bt, kind, color, mid));

  // =========================
  // POWER - القاطع الرئيسي إلى الكونتكترات الستة
  // =========================
  add("cr-src", "L1", "cr-q1", "1", "phase", PHASE_COLORS[0]);
  add("cr-src", "L2", "cr-q1", "3", "phase", PHASE_COLORS[1]);
  add("cr-src", "L3", "cr-q1", "5", "phase", PHASE_COLORS[2]);

  for (const k of ["cr-k1r", "cr-k1l", "cr-k2f", "cr-k2b", "cr-k3u", "cr-k3d"] as const) {
    add("cr-q1", "2", k, "L1", "phase", PHASE_COLORS[0]);
    add("cr-q1", "4", k, "L2", "phase", PHASE_COLORS[1]);
    add("cr-q1", "6", k, "L3", "phase", PHASE_COLORS[2]);
  }

  // محور 1 - يمين: ترتيب طبيعي. يسار: تبديل L1 و L3.
  add("cr-k1r", "T1", "cr-ol1", "L1", "phase", PHASE_COLORS[0]);
  add("cr-k1r", "T2", "cr-ol1", "L2", "phase", PHASE_COLORS[1]);
  add("cr-k1r", "T3", "cr-ol1", "L3", "phase", PHASE_COLORS[2]);
  add("cr-k1l", "T1", "cr-ol1", "L3", "phase", PHASE_COLORS[0]);
  add("cr-k1l", "T2", "cr-ol1", "L2", "phase", PHASE_COLORS[1]);
  add("cr-k1l", "T3", "cr-ol1", "L1", "phase", PHASE_COLORS[2]);
  add("cr-ol1", "T1", "cr-m1", "U1", "phase", PHASE_COLORS[0]);
  add("cr-ol1", "T2", "cr-m1", "V1", "phase", PHASE_COLORS[1]);
  add("cr-ol1", "T3", "cr-m1", "W1", "phase", PHASE_COLORS[2]);

  // محور 2 - أمام طبيعي / خلف تبديل فازتين.
  add("cr-k2f", "T1", "cr-ol2", "L1", "phase", PHASE_COLORS[0]);
  add("cr-k2f", "T2", "cr-ol2", "L2", "phase", PHASE_COLORS[1]);
  add("cr-k2f", "T3", "cr-ol2", "L3", "phase", PHASE_COLORS[2]);
  add("cr-k2b", "T1", "cr-ol2", "L3", "phase", PHASE_COLORS[0]);
  add("cr-k2b", "T2", "cr-ol2", "L2", "phase", PHASE_COLORS[1]);
  add("cr-k2b", "T3", "cr-ol2", "L1", "phase", PHASE_COLORS[2]);
  add("cr-ol2", "T1", "cr-m2", "U1", "phase", PHASE_COLORS[0]);
  add("cr-ol2", "T2", "cr-m2", "V1", "phase", PHASE_COLORS[1]);
  add("cr-ol2", "T3", "cr-m2", "W1", "phase", PHASE_COLORS[2]);

  // محور 3 - أعلى طبيعي / أسفل تبديل فازتين.
  add("cr-k3u", "T1", "cr-ol3", "L1", "phase", PHASE_COLORS[0]);
  add("cr-k3u", "T2", "cr-ol3", "L2", "phase", PHASE_COLORS[1]);
  add("cr-k3u", "T3", "cr-ol3", "L3", "phase", PHASE_COLORS[2]);
  add("cr-k3d", "T1", "cr-ol3", "L3", "phase", PHASE_COLORS[0]);
  add("cr-k3d", "T2", "cr-ol3", "L2", "phase", PHASE_COLORS[1]);
  add("cr-k3d", "T3", "cr-ol3", "L1", "phase", PHASE_COLORS[2]);
  add("cr-ol3", "T1", "cr-m3", "U1", "phase", PHASE_COLORS[0]);
  add("cr-ol3", "T2", "cr-m3", "V1", "phase", PHASE_COLORS[1]);
  add("cr-ol3", "T3", "cr-m3", "W1", "phase", PHASE_COLORS[2]);

  add("cr-src", "PE", "cr-m1", "PE", "earth", EARTH);
  add("cr-src", "PE", "cr-m2", "PE", "earth", EARTH);
  add("cr-src", "PE", "cr-m3", "PE", "earth", EARTH);

  // =========================
  // CONTROL - المصدر + MCB + Emergency Stop مشترك
  // =========================
  add("cr-ctrl", "L", "cr-qc", "1", "control", CONTROL);
  add("cr-qc", "2", "cr-estop", "1", "control", CONTROL);

  // =========================
  // محور 1: يمين / يسار
  // كل اتجاه يمر عبر 95-96 ثم زر لحظي ثم NC للكونتكتر المقابل.
  // لذلك مستحيل يعمل KM1 و KM2 معاً حتى لو ضُغط الزران بنفس الوقت.
  // =========================
  add("cr-estop", "2", "cr-ol1", "95", "control", CONTROL, { x: 1410, y: 500 });
  add("cr-ol1", "96", "cr-br", "3", "control", CONTROL, { x: 1380, y: 580 });
  add("cr-br", "4", "cr-k1l", "21", "control", CONTROL, { x: 1380, y: 650 });
  add("cr-k1l", "22", "cr-k1r", "A1", "control", CONTROL, { x: 1300, y: 500 });

  add("cr-ol1", "96", "cr-bl", "3", "control", CONTROL, { x: 1760, y: 580 });
  add("cr-bl", "4", "cr-k1r", "21", "control", CONTROL, { x: 1760, y: 650 });
  add("cr-k1r", "22", "cr-k1l", "A1", "control", CONTROL, { x: 1340, y: 540 });

  // =========================
  // محور 2: أمام / خلف - إنترلوك متبادل KM3 / KM4
  // =========================
  add("cr-estop", "2", "cr-ol2", "95", "control", CONTROL, { x: 1430, y: 700 });
  add("cr-ol2", "96", "cr-bf", "3", "control", CONTROL, { x: 1380, y: 780 });
  add("cr-bf", "4", "cr-k2b", "21", "control", CONTROL, { x: 1360, y: 840 });
  add("cr-k2b", "22", "cr-k2f", "A1", "control", CONTROL, { x: 1320, y: 650 });

  add("cr-ol2", "96", "cr-bb", "3", "control", CONTROL, { x: 1760, y: 780 });
  add("cr-bb", "4", "cr-k2f", "21", "control", CONTROL, { x: 1760, y: 840 });
  add("cr-k2f", "22", "cr-k2b", "A1", "control", CONTROL, { x: 1360, y: 690 });

  // =========================
  // محور 3: أعلى / أسفل - إنترلوك متبادل KM5 / KM6
  // =========================
  add("cr-estop", "2", "cr-ol3", "95", "control", CONTROL, { x: 1450, y: 900 });
  add("cr-ol3", "96", "cr-bu", "3", "control", CONTROL, { x: 1380, y: 980 });
  add("cr-bu", "4", "cr-k3d", "21", "control", CONTROL, { x: 1360, y: 1040 });
  add("cr-k3d", "22", "cr-k3u", "A1", "control", CONTROL, { x: 1320, y: 800 });

  add("cr-ol3", "96", "cr-bd", "3", "control", CONTROL, { x: 1760, y: 980 });
  add("cr-bd", "4", "cr-k3u", "21", "control", CONTROL, { x: 1760, y: 1040 });
  add("cr-k3u", "22", "cr-k3d", "A1", "control", CONTROL, { x: 1360, y: 840 });

  // نيوترال كويلات الكونتكترات الستة.
  for (const k of ["cr-k1r", "cr-k1l", "cr-k2f", "cr-k2b", "cr-k3u", "cr-k3d"] as const) {
    add("cr-ctrl", "N", k, "A2", "neutral", NEUTRAL, { x: 1930, y: 700 });
  }

  return {
    id: "bridge-crane",
    title: "كرين جسري - Bridge Crane Control",
    description: "3 مواتير: يمين/يسار + أمام/خلف + أعلى/أسفل، مع 6 كونتكترات و3 أوفرلودات وإنترلوك كهربائي يمنع تشغيل الاتجاهين المتعاكسين معاً.",
    doc: {
      name: "مثال - كرين جسري Bridge Crane",
      comps,
      wires,
      notes: [
        note("crn0", 610, -25, "كرين جسري - Bridge Crane Control", 28),
        note("crn1", 95, 325, "M1 حركة الجسر: يمين / يسار"),
        note("crn2", 575, 325, "M2 حركة العربة: أمام / خلف"),
        note("crn3", 1055, 325, "M3 الرفع: أعلى / أسفل"),
        note("crn4", 1500, 500, "Pendant Control - تشغيل لحظي مع Interlock"),
        note("crn5", 1500, 1110, "لا يمكن تشغيل اتجاهين متعاكسين لنفس المحور معاً"),
      ],
    },
  };
}


export const CIRCUIT_EXAMPLES: CircuitExample[] = [
  dolExample(),
  reverseExample(),
  starDeltaExample(),
  sequenceExample(),
  ats2Example(),
  ats3Example(),
  trafficLightExample(),
  bridgeCraneExample(),
];
